<template>
  <div
    class="o-header"
    :class="{ 'menu-fixed': scroll.menuFixed }"
    :style="{ transform: 'translate(0, ' + this.scroll.menuPoint + 'px' }">
    <div class="o-header__right">
      <img
        :alt="logoTooltip"
        :src="logo"
        class="o-header__logo" />
    </div>
    <div class="o-header__nav-button" data-test="header-nav-button" @click="triggerNavButton">
      <icon name="bars" scale="1.75" />
    </div>
    <div class="o-header__title">
      <h2>
        <icon :name="pageIcon" />
        <span data-test="header-title">{{ pageHeader }}</span>
      </h2>
      <h3 data-test="header-subtitle">{{ pageSubHeader }}</h3>
    </div>
    <div class="o-header__profile-menu">
      <v-dropdown-menu :list="dropdownMenu" :onOpened="hideSideNavigation" data-test="header-dropdown-menu">
        <span class="o-header__profile-text">
          <span
            v-if="user"
            class="o-header__profile-text__name"
            data-test="header-name">
              {{ user.name }}
          </span>
          <icon name="angle-down" class="down-icon"></icon>
        </span>

        <div class="o-header__profile-image">
          <div
            class="o-header__profile-image--placeholder"
            data-test="header-initials">
            {{ initials }}
          </div>
        </div>
      </v-dropdown-menu>
    </div>
  </div>
</template>

<script>
import { VDropdownMenu } from '../molecules';

export default {
  name: 'v-header',
  props: {
    navButton: {
      required: false,
      type: [Object, Function],
      default: () => ({}),
    },
    logo: {
      required: true,
      type: [String],
    },
    logoTooltip: {
      required: true,
      type: [String],
    },
    pageIcon: {
      required: true,
      type: [String],
    },
    pageHeader: {
      required: true,
      type: [String],
    },
    pageSubHeader: {
      required: true,
      type: [String],
    },
    user: {
      required: true,
      type: [Object],
    },
    dropdownMenu: {
      required: true,
      type: [Array],
    },
  },
  components: {
    VDropdownMenu,
  },
  data() {
    return {
      scroll: {
        last: null,
        direction: null,
        menuPoint: 0,
        menuFixed: false,
      },
    };
  },
  computed: {
    initials() {
      if (this.user) {
        const match = this.user.name.match(/^\s*(\w)|\b(\w)(?=\S+$)/g);
        return match.join('');
      }

      return '??';
    },
  },
  mounted() {
    window.addEventListener('scroll', this.scrollEvent);
  },
  methods: {
    triggerNavButton() {
      this.scroll.menuPoint = document.documentElement.scrollTop;
      this.navButton();
    },
    hideSideNavigation() {
      this.$bus.$emit('hideSideNavigation');
    },
    scrollEvent() {
      const target = document.documentElement;
      const { last } = this.scroll;
      if (last && target.scrollTop > last) {
        this.scroll.direction = 'down';
        this.scroll.menuFixed = false;
        if (target.scrollTop > 96 && target.scrollTop > this.scroll.menuPoint + 96) {
          this.scroll.menuPoint = target.scrollTop - 96;
        }
      } else if (last && target.scrollTop < last) {
        this.scroll.direction = 'up';
        if (this.scroll.menuPoint > target.scrollTop) {
          this.scroll.menuPoint = target.scrollTop;
          this.scroll.menuFixed = true;
        }
      }
      this.scroll.last = target.scrollTop;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../styles/component-utils';

  $parent: '.o-header';

  $border-color: lighten($signable-secondary-2, 5%);
  $sub-header-color: darken($signable-primary-1, 25%);

  #{$parent} {
    display: block;
    width: 100%;
    height: 84px;
    background: #0098ce;
    color: #fff;
    position: relative;
    z-index: 1;
    &__nav-button {
      display: none;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 15px;
      &:hover, &:active {
        color: $signable-secondary-4-shade-2;
      }
    }
    &__title {
      padding: 16px;
      float: left;
      svg {
        height: 24px;
        width: 24px;
        margin-right: $spacing-small;
        float: left;
      }
      h2 {
        margin-bottom: $spacing-xs;
        font-size: 28px;
        @include clearfix;
        span {
          float: left;
          line-height: 1;
        }
      }
      h3 {
        color: $sub-header-color;
        font-style: italic;
        margin-bottom: 0;
        font-size: 16px;
        clear: left;
      }
    }
    &__right {
      float: left;
      width: 240px;
      padding: 18px;
      text-align: center;
      background: $signable-secondary-2;
      border-bottom: 1px solid $border-color;
      height: 85px;
    }
    &__logo {
      width: 75%;
    }
    &__profile-menu {
      position: absolute;
      top: 50%;
      transform: translate(0, -50%);
      right: $spacing-base;
    }
    &__profile-text {
      display: inline-block;
      vertical-align: middle;
      padding-right: $spacing-small;
      font-weight: 700;
      .down-icon {
        vertical-align: -3px;
        margin-left: $spacing-xs;
      }
    }
    &__profile-image {
      position: relative;
      display: inline-block;
      vertical-align: middle;
      background: $signable-secondary-2;
      border: 1px solid #fff;
      width: 48px;
      height: 48px;
      border-radius: 24px;
      &--placeholder {
        text-align: center;
        font-size: 22px;
        font-weight: bold;
        width: 48px;
        @include alignElement(true, true);
      }
    }
    @media (max-width: 1020px) and (min-width: 451px) {
      position: fixed;
      &__nav-button {
        display: inline-block;
      }
      &__right {
        background: transparent;
        border-bottom: 0;
      }
      &__logo {
        margin-left: 40px;
        width: 60%;
        margin-top: 5px;
      }
      &__title {
        float: none;
        width: 100%;
        display: block;
        background: $signable-secondary-2-shade-1;
        margin-top: 84px;
        height: 42px;
        padding: 0;
        h2 {
          margin: 0;
          font-size: 28px;
          line-height: 42px;
          left: 15px;
          position: absolute;
          span {
            line-height: 42px;
            margin-top: 0;
          }
        }
        h3 {
          display: none;
        }
        svg {
          height: 24px;
          width: 20px;
          margin-right: 10px;
          vertical-align: top;
          margin-top: 8px;
        }
      }
    }
    @media (max-width: 450px) {
      height: 54px;
      position: absolute;
      &__nav-button {
        display: inline-block;
      }
      &__right {
        height: 54px;
        background: transparent;
        border-bottom: 0;
      }
      &__logo {
        width: 50%;
        margin-top: -6px;
        margin-left: 35px;
      }
      &__profile-text__name {
        display: none;
      }
      &__profile-image {
        height: 38px;
        width: 38px;
        &--placeholder {
          width: 38px;
        }
      }
      &__title {
        float: none;
        width: 100%;
        display: block;
        background: $signable-secondary-2-shade-1;
        margin-top: 54px;
        height: 42px;
        padding: 0;
        h2 {
          margin: 0;
          font-size: $font-size-large;
          line-height: 42px;
          left: 15px;
          position: absolute;
          span {
            line-height: 42px;
            margin-top: 0;
          }
        }
        h3 {
          display: none;
        }
        svg {
          height: 24px;
          width: 20px;
          margin-right: 10px;
          vertical-align: top;
          margin-top: 8px;
        }
      }
    }
  }

  /deep/ .m-dropdown-menu__list--active {
    position: fixed;
    right: 0;
    top: 30px !important;
  }

  @media (max-width: 450px) {
    .menu-fixed {
      position: fixed !important;
      transform: translate(0, 0) !important;
    }
  }

</style>
